// ---------------------------------------------------------------------------
// Wires the generic pieces (sceua.ts, objectiveFunction.ts) to this app's
// own engine: re-runs `calculateProject` for a candidate parameter set,
// aggregates the result at ONE outlet point (aggregation.ts), and compares
// it against that outlet point's observed series (storage.ts's
// `observedFlow`) — the calibration run CalibrationPanel.tsx launches.
//
// The observed series and the project's own precipitation series almost
// never share the same date range (the observed one is whatever the user
// pasted — see storage.ts's `observedFlow` header) — `alignToDateAxis`
// reindexes it onto the precipitation series' own date axis (the axis every
// simulated result is already indexed by), `NaN` wherever there's no
// observed value for that date.
//
// Calibration/validation split (CalEvBasic's `PosFinalCalib`/`PosFinalVal`,
// requested by the user after the first 5 phases had already shipped): the
// search (SCE-UA) only ever sees `calibrationStart..calibrationEnd` — it
// never looks at `validationStart..validationEnd`, so `validationIndicators`
// is a real out-of-sample check, not just a second report of the same fit.
// The two windows are fully independent (not a shared A-B-C split point) —
// the user explicitly wants to be able to pick two unrelated periods, not
// just cut one range in two. Passing `options.dateRange` is optional so
// existing callers/tests that don't care about the split keep working:
// without it, the calibration window is the WHOLE precipitation date range
// (the original, pre-split behavior) and `validationIndicators` is simply
// `{}` (nothing was held out).
//
// Each window's series is TRIMMED to that exact date range before being fed
// to `calculateProject` — the search can call `evaluate()` hundreds of
// times (see DEFAULT_UI_MAX_EVALUATIONS), so simulating years of days
// outside the calibration window on every single evaluation would be pure
// waste; validation only runs once, but is trimmed the same way for the
// same reason (and for consistency — one trimming rule, not two). Without
// `dateRange.warmUp`, this still means each run starts from the project's
// own initial state (So/Ro/snow) right at the window's start date, not from
// whatever state a continuous run from the series' true beginning would
// have reached by then. `warmUp` (an amount + days/weeks/months/years unit,
// CalibrationPanel.tsx) fixes this WITHOUT giving up the one-trim-per-window
// rule above: `seriesFor` below extends the trimmed series backward by that
// amount (clamped to whatever history actually exists — `clampedDailyIndex`)
// and simulates it too, but `window.start` is set to right after those
// extra days, so objectiveFunction/indicators.ts never scores them — the
// extra cost this adds is the warm-up days' own simulation time, repeated on
// every `evaluate()` call during the calibration phase (worth knowing if a
// long warm-up on a long-running search turns out slow).
//
// Units: the observed series must already be in the SAME units as the
// project's configured aggregation unit (`units.ts`'s
// `activeUnitsCoefficient`/`activeUnitName`, e.g. "Hm3") — this module
// can't detect or correct a unit mismatch (NSE/Pbias/RSR are not
// scale-invariant, only Pearson is), so CalibrationPanel.tsx surfaces the
// active unit name next to the outlet point picker instead of guessing.
// ---------------------------------------------------------------------------

import { TranslatableError } from "../../i18n/translate.ts";
import { PARAMETER_DEFINITIONS } from "../../model/baseTemplate.ts";
import { calibrationRangeFor } from "../../model/calibrationParameterRanges.ts";
import { trimSeriesByIndexRange, type DailySeries } from "../../model/dailySeries.ts";
import { addDaysIso, addMonthsIso, addYearsIso, daysBetweenIso } from "../../model/isoDate.ts";
import { updateParameterValue } from "../../model/projectEntities.ts";
import type { EvalHidProject } from "../../model/types.ts";
import { activeUnitsCoefficient } from "../../model/units.ts";
import { aggregateContributionsByOutlet } from "../aggregation.ts";
import { calculateProject } from "../engine.ts";
import type { IndicatorWindow } from "./indicators.ts";
import { objectiveFunction, type IndicatorCode } from "./objectiveFunction.ts";
import { runSceUa, type SceUaStopReason } from "./sceua.ts";

/** NSE only — the "reduce the user's options" default (see objectiveFunction.ts's header and project_evalhid_calibracion.md). Not configurable from the UI yet. */
const DEFAULT_WEIGHTS: Partial<Record<IndicatorCode, number>> = { 1: 1 };

/** All 6 indicators, weight 1 — used only to REPORT each one's raw (unweighted) value before/after, never to drive the search (see DEFAULT_WEIGHTS). */
const REPORT_WEIGHTS: Partial<Record<IndicatorCode, number>> = {
  1: 1,
  2: 1,
  3: 1,
  4: 1,
  5: 1,
  6: 1,
};

/**
 * Tuned for an interactive main-thread browser run — NOT SCE-UA's own
 * textbook/VB default (7000, see sceua.ts), which would take too long
 * re-running this app's full engine that many times. `runSceUa` yields
 * periodically (see its own header) so the tab doesn't freeze during the
 * run, but everything still happens on the main thread — a Web Worker
 * would be the next step if a real project turns out too slow even at
 * this reduced budget, not attempted here.
 */
const DEFAULT_UI_MAX_EVALUATIONS = 300;

interface CalibrationTarget {
  outletCode: number;
  zoneCode: number;
  /** Which of the zone's model's parameters to calibrate — the rest keep their current project value. */
  parameterCodes: number[];
}

export type WarmUpUnit = "days" | "weeks" | "months" | "years";

interface CalibrationWarmUp {
  /** Whole, non-negative — 0 (or the field being absent) means no warm-up, same as before this existed. */
  amount: number;
  unit: WarmUpUnit;
}

interface CalibrationDateRange {
  /** ISO date (yyyy-mm-dd), inclusive — first day of the calibration window. */
  calibrationStart: string;
  /** ISO date, inclusive — last day of the calibration window. */
  calibrationEnd: string;
  /** ISO date, inclusive — first day of the validation window. Independent of `calibrationEnd` — the two windows don't have to be adjacent, or even non-overlapping. */
  validationStart: string;
  /** ISO date, inclusive — last day of the validation window. */
  validationEnd: string;
  /**
   * Extra lead-in period simulated (but never scored) right before EACH
   * window's own start — lets the model's internal reservoirs (soil
   * moisture, snowpack, aquifer...) reach a realistic state before the days
   * that actually count toward the objective function, instead of starting
   * from the project's bare default initial state exactly at the window
   * start (see this file's own header). Same amount/unit applied to both
   * the calibration and validation windows, each anchored to its own start
   * date — a "6 months" warm-up means 6 calendar months before
   * `calibrationStart` AND 6 calendar months before `validationStart`,
   * independently. `null`/absent: no warm-up.
   */
  warmUp?: CalibrationWarmUp | null;
}

export interface CalibrationRunResult {
  /** Keyed by parameterCode. */
  bestParameterValues: Record<number, number>;
  bestObjectiveValue: number;
  /** Same objective, evaluated with the project's CURRENT parameter values — what the report compares "after" against. */
  initialObjectiveValue: number;
  /** Every indicator's own raw (unweighted) value BEFORE calibration, over the CALIBRATION window — for the report table, not just the one driving the search. */
  initialIndicators: Partial<Record<IndicatorCode, number>>;
  /** Same, with the best parameters found, still over the calibration window. */
  bestIndicators: Partial<Record<IndicatorCode, number>>;
  /** The best parameters' indicators over the VALIDATION window (`splitDate..validationEnd`) — data the search never saw. `{}` when `options.dateRange` wasn't given (nothing was held out). */
  validationIndicators: Partial<Record<IndicatorCode, number>>;
  /** Days actually simulated (but not scored) right before the calibration window's own start — 0 when no warm-up was requested. */
  calibrationWarmUpDays: number;
  /** True when the loaded series didn't have enough history before the calibration window to fully honor the requested warm-up amount — `calibrationWarmUpDays` is shorter than requested in that case, not an error. */
  calibrationWarmUpClamped: boolean;
  /** Same as `calibrationWarmUpDays`, for the validation window. */
  validationWarmUpDays: number;
  /** Same as `calibrationWarmUpClamped`, for the validation window. */
  validationWarmUpClamped: boolean;
  evaluations: number;
  stopReason: SceUaStopReason;
  history: { evaluations: number; bestValue: number }[];
}

/** Reindexes `values` (on `dates`) onto `targetDates`'s own axis — `NaN` wherever `targetDates` has no matching date. */
export function alignToDateAxis(
  dates: string[],
  values: number[] | Float64Array,
  targetDates: string[]
): number[] {
  const byDate = new Map<string, number>();
  for (let i = 0; i < dates.length; i++) byDate.set(dates[i], values[i]);
  return targetDates.map((d) => byDate.get(d) ?? NaN);
}

/** Throws with a clear message rather than returning -1 — an out-of-range date here means a caller bug (the panel constrains its date inputs to this same axis), not a normal "not found" case a caller should handle silently. */
function dateIndex(dates: string[], iso: string): number {
  const i = dates.indexOf(iso);
  if (i < 0) throw new TranslatableError("calibration.dateOutsidePrecipitation", { date: iso });
  return i;
}

/** `windowStartIso` minus the warm-up amount, in calendar terms for months/years (see addMonthsIso/addYearsIso, isoDate.ts) — `windowStartIso` unchanged when `warmUp` is absent or its amount is 0. */
function warmUpStartIso(
  windowStartIso: string,
  warmUp: CalibrationWarmUp | null | undefined
): string {
  if (!warmUp || warmUp.amount <= 0) return windowStartIso;
  switch (warmUp.unit) {
    case "days":
      return addDaysIso(windowStartIso, -warmUp.amount);
    case "weeks":
      return addDaysIso(windowStartIso, -warmUp.amount * 7);
    case "months":
      return addMonthsIso(windowStartIso, -warmUp.amount);
    case "years":
      return addYearsIso(windowStartIso, -warmUp.amount);
  }
}

/**
 * Index of `iso` on `dates` — a contiguous daily axis starting at
 * `dates[0]` (this app's own invariant for a loaded `DailySeries`, see
 * AemetBulkFetchDialog.tsx's header) — computed by day-offset arithmetic
 * rather than `indexOf`, and clamped to 0 rather than going negative. Used
 * for the warm-up lookback: a request that reaches further back than the
 * loaded series has data should use as much history as IS available
 * instead of throwing (unlike `dateIndex` above, which is for
 * user-chosen dates the panel already constrains to this same axis).
 */
function clampedDailyIndex(dates: string[], iso: string): number {
  return Math.max(0, daysBetweenIso(dates[0], iso));
}

export async function runCalibration(
  project: EvalHidProject,
  seriesForEngine: {
    precipitation: DailySeries;
    etp: DailySeries;
    temperature: DailySeries | null;
  },
  observedFlow: DailySeries,
  target: CalibrationTarget,
  options?: {
    maxEvaluations?: number;
    seed?: number;
    onImprovement?: (evaluations: number, bestValue: number) => void;
    dateRange?: CalibrationDateRange;
  }
): Promise<CalibrationRunResult> {
  const zone = project.zones.find((z) => z.zoneCode === target.zoneCode);
  if (!zone || zone.modelTypeCode == null) {
    throw new TranslatableError("calibration.zoneNotFound");
  }
  const outletPoint = project.outletPoints.find((p) => p.outletCode === target.outletCode);
  if (!outletPoint) throw new TranslatableError("calibration.outletPointNotFound");
  // Captured as a single-element array right after the narrowing check above
  // (not just `outletPoint` itself) because TS's control-flow narrowing
  // doesn't persist into the nested closures below.
  const outletPointArray = [outletPoint];
  const outletColumnIndex = observedFlow.columns.indexOf(outletPoint.name);
  if (outletColumnIndex < 0) {
    throw new TranslatableError("calibration.noObservedSeriesForOutlet");
  }

  const definitions = target.parameterCodes.map((code) => {
    const def = PARAMETER_DEFINITIONS.find(
      (d) => d.modelTypeCode === zone.modelTypeCode && d.kind === 0 && d.parameterCode === code
    );
    if (!def) throw new TranslatableError("calibration.unknownParameter", { code });
    const range = calibrationRangeFor(def);
    if (!range) {
      throw new TranslatableError("calibration.noCalibrationRangeForParameter", {
        name: def.name,
      });
    }
    return { def, range };
  });

  const dateRange = options?.dateRange;
  const warmUp = dateRange?.warmUp;
  const precipDates = seriesForEngine.precipitation.dates;
  const calibrationScoreRange = dateRange
    ? {
        start: dateIndex(precipDates, dateRange.calibrationStart),
        end: dateIndex(precipDates, dateRange.calibrationEnd),
      }
    : { start: 0, end: precipDates.length - 1 };
  const validationScoreRange = dateRange
    ? {
        start: dateIndex(precipDates, dateRange.validationStart),
        end: dateIndex(precipDates, dateRange.validationEnd),
      }
    : null;
  if (calibrationScoreRange.start > calibrationScoreRange.end) {
    // Same message as CalibrationPanel.tsx's own inline validation next to
    // the date pickers — this is the defensive backstop for the same rule.
    throw new TranslatableError("calibration.calibrationDatesOutOfOrder");
  }
  if (validationScoreRange && validationScoreRange.start > validationScoreRange.end) {
    throw new TranslatableError("calibration.validationDatesOutOfOrder");
  }
  const coefficient = activeUnitsCoefficient(project);

  /**
   * Trims precipitation/ETP/temperature to `scoreRange`, extended backward
   * by `warmUp` days (see `warmUpStartIso`/`clampedDailyIndex` above) —
   * every simulation against the returned `series` covers that exact
   * (possibly warm-up-extended) window, re-aligning the observed series
   * onto it once (see this file's own header for why: performance for the
   * repeatedly-called calibration window, consistency for the once-called
   * validation window). `window` is what actually gets SCORED — it starts
   * right after the warm-up days (0 when there's no warm-up, matching the
   * pre-warm-up behavior exactly) and always ends at the trimmed series'
   * own last index, since there's nothing beyond `scoreRange.end` to
   * exclude — objectiveFunction/indicators.ts's own windowing does the
   * actual excluding, just over a series that may now start earlier than
   * what it scores.
   */
  function seriesFor(scoreRange: { start: number; end: number }, windowStartIso: string) {
    const requestedStartIso = warmUpStartIso(windowStartIso, warmUp);
    // min() is a defensive invariant, not something the arithmetic above can
    // actually produce (warmUpStartIso only ever moves backward in time) —
    // never simulate from later than the window's own scored start.
    const simulateFromIndex = Math.min(
      clampedDailyIndex(precipDates, requestedStartIso),
      scoreRange.start
    );
    const trimRange = { start: simulateFromIndex, end: scoreRange.end };
    const series = {
      precipitation: trimSeriesByIndexRange(seriesForEngine.precipitation, trimRange),
      etp: trimSeriesByIndexRange(seriesForEngine.etp, trimRange),
      temperature: seriesForEngine.temperature
        ? trimSeriesByIndexRange(seriesForEngine.temperature, trimRange)
        : null,
    };
    const observedAligned = alignToDateAxis(
      observedFlow.dates,
      observedFlow.values[outletColumnIndex],
      series.precipitation.dates
    );
    const window: IndicatorWindow = {
      start: scoreRange.start - simulateFromIndex,
      end: series.precipitation.dates.length - 1,
    };
    const warmUpDays = scoreRange.start - simulateFromIndex;
    const warmUpClamped = daysBetweenIso(precipDates[0], requestedStartIso) < 0;
    return { series, observedAligned, window, warmUpDays, warmUpClamped };
  }

  const calibration = seriesFor(
    calibrationScoreRange,
    dateRange?.calibrationStart ?? precipDates[0]
  );
  const validation = validationScoreRange
    ? seriesFor(validationScoreRange, dateRange!.validationStart)
    : null;

  function simulateVolume(
    series: { precipitation: DailySeries; etp: DailySeries; temperature: DailySeries | null },
    candidateProject: EvalHidProject
  ): number[] {
    const results = calculateProject(
      candidateProject,
      series.precipitation,
      series.etp,
      series.temperature
    );
    const [contribution] = aggregateContributionsByOutlet(
      results,
      candidateProject.subBasins,
      outletPointArray,
      coefficient
    );
    return Array.from(contribution.volume);
  }

  function projectWithParams(params: number[]): EvalHidProject {
    return params.reduce(
      (p, value, i) =>
        updateParameterValue(p, target.zoneCode, definitions[i].def.parameterCode, 0, value),
      project
    );
  }

  function evaluate(params: number[]): number {
    const simulated = simulateVolume(calibration.series, projectWithParams(params));
    return objectiveFunction(
      DEFAULT_WEIGHTS,
      calibration.observedAligned,
      simulated,
      calibration.window
    ).value;
  }

  function calibrationIndicatorsFor(params: number[]): Partial<Record<IndicatorCode, number>> {
    const simulated = simulateVolume(calibration.series, projectWithParams(params));
    return objectiveFunction(
      REPORT_WEIGHTS,
      calibration.observedAligned,
      simulated,
      calibration.window
    ).indicators;
  }

  function validationIndicatorsFor(params: number[]): Partial<Record<IndicatorCode, number>> {
    if (!validation) return {};
    const simulated = simulateVolume(validation.series, projectWithParams(params));
    return objectiveFunction(
      REPORT_WEIGHTS,
      validation.observedAligned,
      simulated,
      validation.window
    ).indicators;
  }

  const initialParams = definitions.map(({ def }) => {
    const row = project.parameterValues.find(
      (v) => v.zoneCode === target.zoneCode && v.parameterCode === def.parameterCode && v.kind === 0
    );
    return row?.value ?? def.defaultValue ?? 0;
  });
  const initialObjectiveValue = evaluate(initialParams);
  const initialIndicators = calibrationIndicatorsFor(initialParams);

  const result = await runSceUa({
    numParams: definitions.length,
    lowerBounds: definitions.map(({ range }) => range.min),
    upperBounds: definitions.map(({ range }) => range.max),
    evaluate,
    maxEvaluations: options?.maxEvaluations ?? DEFAULT_UI_MAX_EVALUATIONS,
    seed: options?.seed,
    onImprovement: options?.onImprovement,
  });

  const bestParameterValues: Record<number, number> = {};
  definitions.forEach(({ def }, i) => {
    bestParameterValues[def.parameterCode] = result.bestParams[i];
  });

  return {
    bestParameterValues,
    bestObjectiveValue: result.bestValue,
    initialObjectiveValue,
    initialIndicators,
    bestIndicators: calibrationIndicatorsFor(result.bestParams),
    validationIndicators: validationIndicatorsFor(result.bestParams),
    calibrationWarmUpDays: calibration.warmUpDays,
    calibrationWarmUpClamped: calibration.warmUpClamped,
    validationWarmUpDays: validation?.warmUpDays ?? 0,
    validationWarmUpClamped: validation?.warmUpClamped ?? false,
    evaluations: result.evaluations,
    stopReason: result.stopReason,
    history: result.history,
  };
}
